/**
 * Web advertising-consent gate for third-party ad pixels (Roku, and any future
 * web ad network). First-party product analytics are NOT gated here — this
 * covers only third-party advertising trackers.
 *
 * Rules:
 * - Default is "unset" → treated as denied: no ad pixel loads until the
 *   visitor explicitly accepts.
 * - Global Privacy Control (navigator.globalPrivacyControl) forces "denied"
 *   even over a stored opt-in — the browser signal wins.
 * - Choice persists in localStorage and can be changed at any time.
 */

const STORAGE_KEY = "inquira.adconsent";
export const AD_CONSENT_EVENT = "inquira:ad-consent-changed";

export type AdConsent = "granted" | "denied" | "unset";

/** Pure decision logic — exported for unit tests. */
export function resolveAdConsent(stored: string | null, globalPrivacyControl: boolean): AdConsent {
  if (globalPrivacyControl) return "denied";
  if (stored === "granted") return "granted";
  if (stored === "denied") return "denied";
  return "unset";
}

export function getAdConsent(): AdConsent {
  if (typeof window === "undefined") return "denied";
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return "denied";
  }
  const gpc = (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  return resolveAdConsent(stored, gpc);
}

/** True only on an explicit opt-in with no GPC signal. */
export function adTrackingAllowed(): boolean {
  return getAdConsent() === "granted";
}

export function setAdConsent(consent: "granted" | "denied"): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, consent);
  } catch {
    /* private mode — choice applies to this page load only */
  }
  window.dispatchEvent(new Event(AD_CONSENT_EVENT));
}
